// config.js — environment-driven runtime config.
// Replaces the inline window.OPAL_CONFIG block from the prototype HTML. Vite
// inlines `import.meta.env.VITE_*` at build time. The object is also mirrored
// to window.OPAL_CONFIG so existing screen modules can read it without an import.

const env = import.meta.env;
const truthy = (v) => v === true || v === 'true' || v === '1';

function explicitBoolean(value) {
  if (value === true || value === 'true' || value === '1') return true;
  if (value === false || value === 'false' || value === '0') return false;
  return null;
}

function currentHostname() {
  if (typeof window === 'undefined') return '';
  return window.location?.hostname || '';
}

export function resolveRevenueCatSandboxMode({ hostname = currentHostname(), envValue = env.VITE_USE_SANDBOX } = {}) {
  const explicit = explicitBoolean(envValue);
  if (explicit !== null) return explicit;

  const host = String(hostname || '').toLowerCase().split(':')[0];

  // All Vercel-generated domains — production or preview deployment URLs —
  // must stay in RevenueCat sandbox. Real money only happens on custom Opal
  // domains such as start.opalapp.com.
  if (!host || host === 'localhost' || host === '127.0.0.1' || host === '0.0.0.0') return true;
  if (host.endsWith('.localhost') || host.endsWith('.vercel.app')) return true;

  // Any non-Vercel custom host defaults to production. Use
  // VITE_USE_SANDBOX=true to force sandbox for a custom staging domain.
  return false;
}

const useSandbox = resolveRevenueCatSandboxMode();

const cfg = {
  RC_WEB_KEY_SANDBOX:    env.VITE_RC_KEY_SANDBOX || '',
  RC_WEB_KEY_PROD:       env.VITE_RC_KEY_PROD || '',
  USE_SANDBOX:           useSandbox,
  RC_ENV_SOURCE:         explicitBoolean(env.VITE_USE_SANDBOX) === null ? 'hostname' : 'env',
  RC_TARGET_OFFERING:    env.VITE_RC_TARGET_OFFERING || '',
  AMPLITUDE_API_KEY:        env.VITE_AMPLITUDE_API_KEY || null,
  AMPLITUDE_API_KEY_LEGACY: env.VITE_AMPLITUDE_API_KEY_LEGACY || null,
  AMPLITUDE_SERVER_ZONE:    env.VITE_AMPLITUDE_SERVER_ZONE || 'US',
  AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY: env.VITE_AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY || null,
  // Experiment can live in a different region than Analytics. Defaults to the
  // Analytics zone for convenience; set explicitly when they differ. Opal
  // currently runs both on US, but the explicit Experiment zone is kept as
  // a guardrail against silent inheritance if Analytics is ever moved.
  AMPLITUDE_EXPERIMENT_SERVER_ZONE: env.VITE_AMPLITUDE_EXPERIMENT_SERVER_ZONE
    || env.VITE_AMPLITUDE_SERVER_ZONE
    || 'US',
  META_PIXEL_ID:         env.VITE_META_PIXEL_ID || null,
  CAPI_ENDPOINT:         env.VITE_CAPI_ENDPOINT || null,
  // Google Ads — gtag.js loads only when the AW- id is set. Each conversion
  // action created in the Google Ads UI gets its own label; a conversion only
  // fires when its label is configured, so labels can roll out one at a time.
  GOOGLE_ADS_ID:         env.VITE_GOOGLE_ADS_ID || null,
  GOOGLE_ADS_LABELS: {
    lead:     env.VITE_GOOGLE_ADS_LABEL_LEAD || null,
    checkout: env.VITE_GOOGLE_ADS_LABEL_CHECKOUT || null,
    trial:    env.VITE_GOOGLE_ADS_LABEL_TRIAL || null,
    purchase: env.VITE_GOOGLE_ADS_LABEL_PURCHASE || null,
  },
  UPSELL_ENABLED:        truthy(env.VITE_UPSELL_ENABLED),
  APP_STORE_FALLBACK:    env.VITE_APP_STORE_FALLBACK || 'https://apps.apple.com/app/opal-screen-time-focus/id1497465230',
  DEBUG:                 truthy(env.VITE_DEBUG) || env.DEV,
};
cfg.RC_WEB_KEY = cfg.USE_SANDBOX ? cfg.RC_WEB_KEY_SANDBOX : cfg.RC_WEB_KEY_PROD;

if (typeof window !== 'undefined') window.OPAL_CONFIG = cfg;

export default cfg;
